﻿import React, { ReactNode, useState, useRef } from 'react'
import * as yup from "yup";
import { NextPrevious } from './NextPrevious'
import { useValidation, SetState, Validate } from '../validation'
import { ErrorState } from '../Types';
import { FileDownloadFormats, FormContext, FormPageContext, FormGridMehods } from './Types';
import { SaveAndDownload } from './SaveAndDownload';

export function useFakeValidation<M extends {}>(
  model: M,
  onChange: (v: M) => any
): [SetState<M>, ErrorState<M>, Validate] {
  const errors: ErrorState<M> = {};
  const setData: SetState<M> = (m: Partial<M>) => { onChange({ ...model, ...m }) }
  const validate: Validate = async () => true;
  return [setData, errors, validate];
}

export interface FormPageProps<M extends {}> {
  ctx: FormContext<M>;
  schema?: yup.ObjectSchema<M>;
  onNext?: (m: M) => Promise<boolean>;
  children?: (ctx: FormPageContext<M>) => ReactNode;
}

export const FormPage = <M extends {}>(props: FormPageProps<M>) => {
  const { model, onChange, getNextPage, getPreviousPage, page, onNavigate, readOnly, onSaveEnabled, ...rest } = props.ctx;

  const [setModel, errors, validate] = props.schema ?
    useValidation<M>(props.schema, model, onChange) : useFakeValidation(model, onChange);

  const containerId = "form-container"
  const containerSelector = `#${containerId}`

  const nextPageId = getNextPage(page, model);
  const previousPageId = getPreviousPage(page, model);

  // Configure grid support
  const [gridErrors, setGridErrors] = useState<boolean>(false);
  const gridRefs = useRef<{ [key: number]: FormGridMehods<M, any> }>({});
  const resetErrorsFlag = async () => {
    if (!gridErrors) {
      return;
    }
    for (let id in gridRefs.current) {
      let grid = gridRefs.current[id];
      const [, invalid] = await grid.getUncommited(false, false);
      if (invalid) {
        return;
      }
    }
    setGridErrors(false);
  }

  const onNext = async () => {
    var res = await validate(containerSelector);

    // Grid
    var newModel = props.ctx.model;
    for (let id in gridRefs.current) {
      let grid = gridRefs.current[id];
      const [newRows, invalidRows] = await grid.getUncommited(true, false);
      newModel = grid.setRows(newModel, newRows)

      if (invalidRows) {
        setGridErrors(true);
        res = false;
      }
    }

    // Page
    if (res) {
      if(props.onNext && !(await props.onNext(newModel))) {
        onChange(newModel);
      }
      else if (page == nextPageId) {
        onNavigate(newModel, true, true)
      }
      else {
        onNavigate(newModel, nextPageId)
      }
    }
  }

  const onPrevious = async () => {
    // Grid
    let newModel = props.ctx.model;
    for (let id in gridRefs.current) {
      let grid = gridRefs.current[id];
      let [newRows] = await grid.getUncommited(true, true);
      newModel = grid.setRows(newModel, newRows);
    }

    // Page
    onNavigate(newModel, previousPageId);
  }

  const onPrint = async (format: FileDownloadFormats) => {
    if (!rest.onPrint) {
      return;
    }

    // Grid
    let newModel = props.ctx.model;
    for (let id in gridRefs.current) {
      let grid = gridRefs.current[id];
      let [newRows] = await grid.getUncommited(false, false);
      newModel = grid.setRows(newModel, newRows);
    }

    // Page
    rest.onPrint(newModel, format);
  }

  const onSave = async (exit: boolean) => {
    // Grid
    let newModel = props.ctx.model;
    for (let id in gridRefs.current) {
      let grid = gridRefs.current[id];
      let [newRows] = await grid.getUncommited(true, true);
      newModel = grid.setRows(newModel, newRows);
    }

    await onNavigate(newModel, exit);

    if (!exit) {
      for (let id in gridRefs.current) {
        let grid = gridRefs.current[id];
        grid.revalidate();
      }
    }
  }

  return <>
    <div id={containerId}>
      {props.children ? props.children({ ...props.ctx, model, setModel, errors, validate, gridRefs, resetErrorsFlag }) : <></>}
    </div>
    <NextPrevious
      onPrevious={previousPageId == page ? undefined : onPrevious}
      onNext={nextPageId == page && readOnly ? undefined : onNext}
      next={nextPageId == page ? "Submit" : "Next"}
      className="mt-4"
      errors={gridErrors ? gridErrors : errors} />
    <SaveAndDownload
      readOnly={readOnly}
      onPrint={rest.onPrint ? onPrint : undefined}
      onSave={onSaveEnabled ? onSave : undefined}
      additionalDownloadButtons={props.ctx.additionalDownloadButtons}
    />
  </>
}